
import React, { useState, useEffect, useMemo } from 'react';
import { CopyIcon } from './icons/CopyIcon';
import type { User } from '../types';

interface ScriptDisplayProps {
  script: string;
  isLoading: boolean;
  error: string | null;
  user: User;
  onSave: () => void;
  isSaving?: boolean;
}

const ScriptSection: React.FC<{ title: string; content: string }> = ({ title, content }) => (
  <div className="mb-6 bg-black/20 rounded-lg border-l-4 border-[#f15a24] overflow-hidden shadow-md">
    <div className="bg-[#002d5b]/60 px-4 py-3 flex items-center justify-between border-b border-[#f15a24]/10">
      <h3 className="text-xs font-black text-[#f15a24] uppercase tracking-widest">
        {title}
      </h3>
    </div>
    <div className="p-5 text-gray-100 whitespace-pre-wrap leading-relaxed font-medium text-sm">
      {content}
    </div>
  </div>
);

const LoadingSkeleton: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-16 space-y-6">
    {/* Pulsing AI icon */}
    <div className="relative">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#3db8c5] to-[#002d5b] animate-pulse flex items-center justify-center">
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <div className="absolute inset-0 rounded-full bg-[#3db8c5] animate-ping opacity-20"></div>
    </div>

    {/* Pulsing text */}
    <div className="text-center space-y-2">
      <p className="text-xl font-bold text-white animate-loading-pulse">
        Generating your script
        <span className="inline-block animate-pulse">...</span>
      </p>
      <p className="text-sm text-gray-400">Our AI is crafting something viral</p>
    </div>

    {/* Progress bar animation */}
    <div className="w-48 h-1 bg-[#002d5b] rounded-full overflow-hidden">
      <div className="h-full bg-gradient-to-r from-[#3db8c5] to-[#f15a24] animate-shimmer" style={{ width: '100%' }}></div>
    </div>
  </div>
);

export const ScriptDisplay: React.FC<ScriptDisplayProps> = ({ script, isLoading, error, user, onSave, isSaving }) => {
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => setCopySuccess(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [copySuccess]);

  const handleCopy = () => {
    navigator.clipboard.writeText(script).then(() => setCopySuccess('COPIED!'), () => setCopySuccess('FAILED'));
  };

  const parsedSections = useMemo(() => {
    if (!script) return [];
    const headerRegex = /\*\*(.*?)\*\*:?/g;
    const sections: { title: string; content: string }[] = [];
    const matches = Array.from(script.matchAll(headerRegex));
    if (matches.length === 0) return [];
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const title = match[1].trim();
      const start = match.index! + match[0].length;
      const end = matches[i + 1] ? matches[i + 1].index : script.length;
      const content = script.substring(start, end).trim();
      if (title && content) sections.push({ title, content });
    }
    return sections;
  }, [script]);

  return (
    <div className="relative h-full flex flex-col group">
      <div className="absolute top-2 left-2 w-full h-full bg-[#3db8c5] z-0"></div>
      <div className="relative z-10 bg-[#002d5b] border-2 border-[#f15a24] outline outline-[5px] outline-white p-6 lg:p-8 h-full flex flex-col">

        <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-[#f15a24]/30">
          <div className="flex items-center">
            <div className="w-3 h-8 bg-[#f15a24] mr-3 skew-x-[-12deg]"></div>
            <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Output Log</h2>
          </div>
        </div>

        <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar min-h-[400px]">
          {isLoading ? <LoadingSkeleton /> : error ? <div className="p-6 bg-red-500/10 text-red-200 rounded-xl text-center font-bold border-2 border-red-500">{error}</div> : script ? (
            <div className="animate-fade-in space-y-4">
              <div className="flex flex-wrap gap-2 justify-end mb-2 sticky top-0 z-20 bg-[#002d5b]/90 py-2">
                {user.isLoggedIn && (
                  <button
                    onClick={onSave}
                    disabled={isSaving}
                    className="flex items-center space-x-2 bg-[#3db8c5] hover:bg-[#2fa3b0] text-white px-4 py-2 rounded font-black uppercase text-xs tracking-wider transition-colors shadow-lg border border-[#3db8c5] btn-hover-scale"
                  >
                    <span>{isSaving ? 'Saving...' : 'Save Template'}</span>
                  </button>
                )}
                <button
                  onClick={handleCopy}
                  className={`flex items-center space-x-2 bg-[#f15a24] hover:bg-[#d44d1b] text-white px-4 py-2 rounded font-black uppercase text-xs tracking-wider transition-colors shadow-lg border border-[#f15a24] btn-hover-scale ${copySuccess ? 'animate-copy-success' : ''}`}
                >
                  {copySuccess ? <span>✓ {copySuccess}</span> : <><CopyIcon className="w-4 h-4" /><span>Copy Script</span></>}
                </button>
              </div>

              <div className="space-y-1">
                {parsedSections.length > 0 ? parsedSections.map((section, idx) => <ScriptSection key={idx} title={section.title} content={section.content} />) : <div className="whitespace-pre-wrap font-medium text-sm p-6 bg-black/20 rounded border border-[#3db8c5]/20 leading-relaxed text-gray-200">{script}</div>}
              </div>
            </div>
          ) : (
            <div className="text-center flex flex-col items-center justify-center h-full opacity-40">
              <p className="text-gray-400 font-bold uppercase tracking-wide">Ready for Transmission</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
